import React, { useState, useEffect } from 'react';
import {
  ArrowRight, Check, Shield, Globe, Building2, Scale, FileText, TrendingUp,
  Phone, Mail, MapPin, Clock, Users, Briefcase, ChevronDown, ChevronUp, Star,
  ChevronLeft, ChevronRight, BookOpen
} from 'lucide-react';
import { Button } from './ui/button';
import { AppointmentSection } from './AppointmentSection';
import ChatWidget from './ChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FDIBlogScroller = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/blogs/fdi?limit=50`)
      .then(r => r.json())
      .then(data => { setBlogs(data.blogs || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 340, behavior: 'smooth' });
    }
  };

  if (loading || blogs.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50" data-testid="fdi-blog-scroller">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">FDI Insights & Research</h2>
            <p className="text-gray-600 mt-1">{blogs.length}+ articles published since 2021</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => scroll(-1)} data-testid="blog-scroll-left"
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-emerald-50 hover:border-emerald-400 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => scroll(1)} data-testid="blog-scroll-right"
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-emerald-50 hover:border-emerald-400 transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {blogs.map((blog, i) => {
            const date = blog.createdAt ? new Date(blog.createdAt) : null;
            return (
              <a href={`/blog/${blog.slug}`} key={blog.slug || i}
                data-testid={`fdi-blog-card-${i}`}
                className="flex-shrink-0 w-[310px] snap-start bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all group">
                <div className="p-5 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">FDI</span>
                    {date && <span className="text-xs text-gray-400">{date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</span>}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 group-hover:text-emerald-700 transition-colors line-clamp-2">
                    {blog.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed flex-1 line-clamp-3">{blog.excerpt}</p>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <BookOpen className="w-3 h-3" /> {blog.views || 0} views
                    </span>
                    <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div className="text-center mt-8">
          <a href="/blogs" className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
            data-testid="fdi-view-all-blogs">
            View All Articles <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

const FDI_PACKAGES = [
  {
    name: 'Essential',
    price: '1,25,000',
    description: 'For startups & small foreign companies entering India',
    features: [
      { category: 'Incorporation', items: [
        'Private Limited Company incorporation',
        'Digital Signature Certificates (DSC)',
        'Director Identification Numbers (DIN)',
        'PAN & TAN registration',
        'GST registration'
      ]},
      { category: 'FDI Compliance', items: [
        'RBI FDI reporting (FC-GPR)',
        'DPIIT registration',
        'Basic FEMA compliance'
      ]},
      { category: 'Support', items: [
        'Registered office address (1 year)',
        'Bank account opening assistance',
        'Email support for 3 months'
      ]}
    ],
    timeline: '15-20 business days'
  },
  {
    name: 'Professional',
    price: '2,50,000',
    badge: 'Recommended',
    description: 'For mid-size MNCs setting up Indian operations',
    features: [
      { category: 'Everything in Essential, plus:', items: [] },
      { category: 'Advanced Setup', items: [
        'Entry strategy consulting (2 sessions)',
        'Branch/Liaison/Project Office setup options',
        'Shareholder agreements drafting',
        'Board resolution templates',
        'Compliance calendar setup'
      ]},
      { category: 'FDI & Tax', items: [
        'Transfer pricing initial documentation',
        'Advance tax planning',
        'FEMA compliance review',
        'Exit strategy framework'
      ]},
      { category: 'Ongoing Support', items: [
        'Virtual office address (1 year)',
        'Dedicated relationship manager',
        '6 months post-incorporation support'
      ]}
    ],
    timeline: '20-25 business days'
  },
  {
    name: 'Enterprise',
    price: '4,50,000',
    description: 'For large MNCs & wholly-owned subsidiaries',
    features: [
      { category: 'Everything in Professional, plus:', items: [] },
      { category: 'Full-Service Setup', items: [
        'Comprehensive entry & exit strategy',
        'Multiple entity structure planning',
        'Joint venture agreement support',
        'Intellectual property registration guidance',
        'Employment agreements & HR compliance setup'
      ]},
      { category: 'Premium Compliance', items: [
        'Complete transfer pricing documentation',
        'Annual FEMA compliance',
        'RBI reporting (all forms)',
        'Statutory audit coordination',
        'Quarterly compliance reviews'
      ]},
      { category: 'White-Glove Support', items: [
        'Dedicated CA partner',
        '12 months comprehensive support',
        'Board meeting support',
        'Investor/parent company reporting'
      ]}
    ],
    timeline: '25-30 business days'
  }
];

const FDI_FAQS = [
  {
    q: "What types of entities can a foreign company set up in India?",
    a: "Foreign companies can establish a Private Limited Company (wholly-owned subsidiary), Branch Office, Liaison Office, or Project Office. A Private Limited Company is most common for FDI as it allows full business operations and profit repatriation."
  },
  {
    q: "How long does it take to incorporate a company in India under FDI?",
    a: "Typically 15-30 business days depending on the structure. This includes name approval (2-3 days), DSC/DIN (3-5 days), incorporation certificate (5-7 days), and post-incorporation registrations (PAN, TAN, GST, bank account)."
  },
  {
    q: "What is the minimum capital requirement for FDI in India?",
    a: "There's no minimum capital requirement for most sectors under the automatic route. However, for Branch/Liaison offices, RBI may require a track record of profitability. We recommend at least ₹1 lakh authorized capital for a Private Limited Company."
  },
  {
    q: "Can we repatriate profits from our Indian subsidiary?",
    a: "Yes, profits can be repatriated through dividends after paying applicable taxes (currently no Dividend Distribution Tax, but TDS applies). We help structure profit repatriation to minimize tax impact while ensuring full FEMA/RBI compliance."
  },
  {
    q: "What ongoing compliance is required after incorporation?",
    a: "Annual filings include ROC returns (MCA), income tax returns, GST returns, TDS returns, transfer pricing documentation, RBI FDI reporting, and statutory audit. Our packages include ongoing support to handle all compliance."
  }
];

const FDILandingPage = () => {
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const [interestedPlan, setInterestedPlan] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  const handleBook = (planName) => {
    if (planName && typeof planName === 'string') setInterestedPlan(planName);
    setIsAppointmentOpen(true);
    setShowPopup(false);
  };

  useEffect(() => {
    document.title = 'India Entry & FDI Incorporation | Virtual CFO Consultancy';
  }, []);

  return (
    <>
      <meta name="description" content="Set up your company in India under FDI route. Complete incorporation, FEMA/RBI compliance, and entry-exit strategy by Chartered Accountants. 3 packages starting ₹2.5L." />

      <div className="min-h-screen bg-white">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-20 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
          </div>

          {/* Nav */}
          <nav className="relative z-10 max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <a href="/" className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Virtual CFO Consultancy</a>
            <div className="flex items-center gap-4">
              <a href="/" className="text-sm text-gray-300 hover:text-white transition-colors">Main Site</a>
              <Button onClick={handleBook} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">
                <Phone className="w-4 h-4 mr-1" /> Book a Call
              </Button>
            </div>
          </nav>

          <div className="relative z-10 max-w-7xl mx-auto px-4 py-16 md:py-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-emerald-500/30">
                <Globe className="w-4 h-4" />
                India Entry & FDI Advisory
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="fdi-hero-title">
                Enter India with
                <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  Confidence & Compliance
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl">
                Complete company incorporation, FDI compliance, and entry-exit strategy for MNCs
                and wholly-owned subsidiaries — handled by India's experienced Chartered Accountants.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={handleBook} size="lg" data-testid="fdi-hero-cta"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg rounded-full">
                  Get Your Free Strategy Session <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <a href="https://wa.me/917977789741" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 px-8 py-4 rounded-full text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp Now
                </a>
              </div>

              <div className="flex flex-wrap gap-6 mt-10 text-sm text-gray-400">
                <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> 100% FDI Compliant</span>
                <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> FEMA & RBI Experts</span>
                <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> 15-Day Fast Track</span>
              </div>
            </div>
          </div>
        </section>

        {/* Entity Types */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Choose Your India Entry Structure</h2>
            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">We help you pick the right entity type based on your business goals, investment size, and operational needs.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Building2, title: 'Private Limited', desc: 'Full business operations, ideal for wholly-owned subsidiaries', best: 'Most Popular for FDI' },
                { icon: Globe, title: 'Branch Office', desc: 'Extension of parent company for specific activities', best: 'Trade & Services' },
                { icon: Briefcase, title: 'Liaison Office', desc: 'Representational activities, no revenue generation in India', best: 'Market Exploration' },
                { icon: FileText, title: 'LLP', desc: 'Limited liability with operational flexibility', best: 'Small Operations' }
              ].map((entity, i) => (
                <div key={entity.title || `entity-${i}`} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all">
                  <entity.icon className="w-8 h-8 text-emerald-600 mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">{entity.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{entity.desc}</p>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">{entity.best}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="py-16 px-4" id="packages">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">India Incorporation Packages</h2>
            <p className="text-center text-gray-600 mb-10">All-inclusive pricing. No hidden fees.</p>

            <div className="grid lg:grid-cols-3 gap-8">
              {FDI_PACKAGES.map((pkg, i) => (
                <div key={pkg.name || `pkg-${i}`} data-testid={`fdi-package-${pkg.name.toLowerCase()}`}
                  className={`relative rounded-2xl border-2 overflow-hidden transition-all hover:-translate-y-1 ${
                    pkg.badge ? 'border-emerald-500 shadow-2xl' : 'border-gray-200 hover:border-emerald-300 hover:shadow-lg'
                  }`}>
                  {pkg.badge && (
                    <div className="bg-emerald-600 text-white text-center py-2 text-sm font-semibold flex items-center justify-center gap-1">
                      <Star className="w-4 h-4" /> {pkg.badge}
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{pkg.name}</h3>
                    <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
                    <div className="flex items-baseline mb-1">
                      <span className="text-3xl font-bold text-gray-900">{'\u20B9'}{pkg.price}</span>
                      <span className="text-gray-500 ml-2">one-time</span>
                    </div>
                    <p className="text-xs text-emerald-600 mb-6 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Timeline: {pkg.timeline}
                    </p>

                    <Button onClick={() => handleBook(`FDI ${pkg.name}`)} className={`w-full mb-6 py-5 rounded-xl font-semibold ${
                      pkg.badge
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white'
                    }`}>
                      Get Started <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>

                    <div className="space-y-4">
                      {pkg.features.map((cat, ci) => (
                        <div key={ci}>
                          <h4 className={`text-xs font-bold mb-2 uppercase tracking-wide ${ci === 0 && cat.items.length === 0 ? 'text-emerald-600' : 'text-gray-800'}`}>
                            {cat.category}
                          </h4>
                          <ul className="space-y-1.5">
                            {cat.items.map((f, fi) => (
                              <li key={fi} className="flex items-start text-sm text-gray-700">
                                <Check className="w-4 h-4 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                                {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Virtual CFO Consultancy */}
        <section className="py-16 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-10">Why MNCs Choose Virtual CFO Consultancy</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Shield, title: '100% Compliant', desc: 'RBI, FEMA, Companies Act — we handle it all' },
                { icon: Users, title: 'CA-Led Team', desc: 'Chartered Accountants with FDI specialization' },
                { icon: TrendingUp, title: '50+ FDI Setups', desc: 'Successfully incorporated companies from 12+ countries' },
                { icon: Scale, title: 'Entry + Exit', desc: 'Complete lifecycle advisory, not just incorporation' }
              ].map((item, i) => (
                <div key={item.title || `item-${i}`} className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <item.icon className="w-8 h-8 text-emerald-400 mb-3" />
                  <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FDI Blog Scroller */}
        <FDIBlogScroller />

        {/* FAQ */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">FDI & Incorporation FAQs</h2>
            <div className="space-y-3">
              {FDI_FAQS.map((faq, i) => (
                <div key={faq.q?.slice(0,20) || `faq-${i}`} className={`bg-white rounded-xl border ${openFaq === i ? 'border-emerald-300 shadow-lg' : 'border-gray-200'} overflow-hidden`}
                  data-testid={`fdi-faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50">
                    <span className={`font-semibold pr-4 ${openFaq === i ? 'text-emerald-700' : 'text-gray-900'}`}>{faq.q}</span>
                    {openFaq === i ? <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                  </button>
                  {openFaq === i && <div className="px-6 pb-4"><p className="text-gray-600 leading-relaxed">{faq.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-center px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Enter India?</h2>
            <p className="text-emerald-100 text-lg mb-8">
              Book a free strategy session with our FDI experts. We'll analyze your entry options and
              recommend the best structure for your business.
            </p>
            <Button onClick={handleBook} size="lg" data-testid="fdi-final-cta"
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-10 py-6 text-lg rounded-full font-semibold">
              Book Free Strategy Session <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-gray-400 py-8 px-4 text-center text-sm">
          <p>&copy; {new Date().getFullYear()} NSRM Consultants India Private Limited. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-3">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <a href="/blogs" className="hover:text-white transition-colors">Blog</a>
            <a href="https://wa.me/917977789741" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp</a>
          </div>
        </footer>

        {/* Popup */}
        {showPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowPopup(false)}>
            <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl relative" onClick={e => e.stopPropagation()} data-testid="fdi-popup">
              <button onClick={() => setShowPopup(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <span className="text-xl">&times;</span>
              </button>
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Entering India via FDI?</h3>
                <p className="text-gray-600 mb-6">
                  Get a personalized entry strategy with 3 best incorporation options tailored for your business.
                  Free 45-minute consultation with our FDI expert CAs.
                </p>
                <div className="space-y-3 text-left mb-6">
                  {['Customized incorporation structure', 'Entry & exit strategy roadmap', 'Complete compliance checklist'].map((item, i) => (
                    <div key={`feature-${i}`} className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> {item}
                    </div>
                  ))}
                </div>
                <Button onClick={handleBook} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5 text-lg rounded-full font-semibold" data-testid="fdi-popup-cta">
                  Get 3 Best Offers <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-xs text-gray-500 mt-3">Free consultation. No obligation.</p>
              </div>
            </div>
          </div>
        )}

        <AppointmentSection isOpen={isAppointmentOpen} onClose={() => setIsAppointmentOpen(false)} interestedPlan={interestedPlan} />
        <ChatWidget onBookAppointment={handleBook} />
      </div>
    </>
  );
};

export default FDILandingPage;
